// @flow
import React, { Fragment } from 'react'
import styled, { css } from 'styled-components'
import { Input } from 'antd'
import { fontSize, spacing } from 'frontend_core/dist/styles/variables'
import { mediaMax, mediaMin } from 'frontend_core/dist/styles/media'
import Colors from 'components/styles/colors'
import FloatBottom from 'components/molecules/FloatBottom'
import FormSectionTitle from 'components/molecules/FormSectionTitle'
import ReputationStar from 'components/molecules/ReputationStar'
import Button from 'components/atoms/Button'
import Title from 'components/atoms/Title'
import LinkText from 'components/atoms/LinkText'
import Icon from 'components/atoms/Icon'
import InputContainer from 'components/molecules/InputContainer'
import FormItem from 'components/molecules/FormItem'
import CheckboxList from 'components/molecules/CheckboxList'
import RadioList from 'components/molecules/RadioList'
import HeadlineReservation from 'components/molecules/HeadlineReservation'

const Section = styled.div`
  margin-top: ${spacing.large}px;
`
const SubSection = styled.div`
  margin-top: ${spacing.small_3}px;
  border-top: 1px solid ${Colors.grayE};
`
const LinkTextWrapper = styled.div`
  margin-top: ${spacing.small_3}px;
`
const SectionSubTitleWrapper = styled.div`
  margin-top: ${spacing.medium_2}px;
`
const SectionSubTitle = styled.p`
  font-size: ${fontSize.small_3}px;
  font-weight: bold;
`
const ItemWrapper = styled.div`
  margin-top: ${spacing.small_3}px;
`
const FormWrapper = styled.div`
  margin-top: ${spacing.medium}px;
`
const CheckboxListWrapper = styled.div`
  margin-top: ${spacing.medium_2}px;
`
const ButtonWrapper = styled.span`
  display: inline-block;
  width: calc((100% - 8px) / 2);

  ${mediaMin.phone`
    width: 160px;
    font-size: ${fontSize.small_3}px;
  `};

  &:not(:first-child) {
    margin-left: ${spacing.small_2}px;
  }
`
const AttentionWrapper = styled.div`
  margin-top: ${spacing.large}px;
`
const AttentionTitle = styled.p`
  font-size: ${fontSize.medium}px;
`
const AttentionBlock = styled.div`
  margin-top: ${spacing.small_3}px;
  ${mediaMin.phone`
    margin-top: ${spacing.medium}px;
  `};
`
const SectionDesc = styled.p`
  margin-top: ${spacing.small_2}px;
  font-size: ${fontSize.small_2}px;
  color: ${Colors.gray9};
`
const RadioListRecommendedWrapper = styled.div`
  margin-top: ${spacing.medium_3}px;
`
const InputWrapper = styled.div`
  display: none;
  ${({ isActive }) =>
    isActive &&
    css`
      display: block;
      margin-top: ${spacing.medium}px;
    `};
`
const Attention = styled.pre`
  margin-top: ${spacing.medium_2}px;
  text-align: center;
  color: ${Colors.red};
  font-weight: bold;
  font-size: ${fontSize.small_3}px;
  line-height: 1.4;
  white-space: pre-wrap;
  ${mediaMin.phone`
    margin-top: ${spacing.large}px;
  `};
`
const BrSp = styled.br`
  ${mediaMin.phone`
    display: none;
  `};
`
const Em = styled.em`
  color: ${Colors.red};
`
const Description = styled.p`
  padding-left: 1em;
  text-indent: -1em;
  font-size: ${fontSize.small_2}px;
  color: ${Colors.black};
  line-height: 1.285;
  ${mediaMin.phone`
    font-size: ${fontSize.small_3}px;
  `};
  :not(:first-of-type) {
    margin-top: 6px;
  }
`
const TitleDesc = styled.p`
  margin-top: ${spacing.small_3}px;
  color: ${Colors.gray9};
  font-size: ${fontSize.small_2}px;
  line-height: 1.424;
`
const TitleDescAttention = styled.em`
  color: ${Colors.red};
`
const TitleDescAttentionEm = styled.em`
  font-weight: bold;
`

export type PropTypes = {
  cancelReason?: ?number,
  onChangeCancelReason: Function,
  isActiveReasonDesc?: boolean,
  reasonDesc?: ?string,
  errorReasonDesc?: ?string,
  onClickBackToMessage: Function,
  onClickSend: Function,
  isSendButtonDisabled?: ?boolean,
  hrefCancelPolicy?: string,
}

export default function ReservationCancelForm(props: PropTypes) {
  return (
    <Fragment>
      <HeadlineReservation
        title={i18n.t('page.dashboard.reservation.reservationCancel')}
        linkText={i18n.t('page.dashboard.reservation.backToMessage')}
        href="#"
        onClick={props.onClickBackToMessage}
      >
        {i18n.t('page.dashboard.reservation.cancelDesc.0')}
        <a href={props.hrefCancelPolicy}>
          {i18n.t('page.dashboard.reservation.cancelDesc.1')}
        </a>
        {i18n.t('page.dashboard.reservation.cancelDesc.2')}
        <br />
        <TitleDescAttention>
          {i18n.t('page.dashboard.reservation.cancelTransfer.0')}
          <TitleDescAttentionEm>
            {i18n.t('page.dashboard.reservation.cancelTransfer.1')}
          </TitleDescAttentionEm>
          {i18n.t('page.dashboard.reservation.cancelTransfer.2')}
        </TitleDescAttention>
      </HeadlineReservation>
      <Section>
        <FormSectionTitle
          title={i18n.t('page.dashboard.reservation.cancelReason')}
          description={i18n.t('page.dashboard.reservation.cancelReasonDesc')}
          required
        />
        <FormWrapper>
          <RadioList
            radioList={[
              {
                label: i18n.t(
                  'page.dashboard.reservation.doubleBookingReservation',
                ),
                value: 1,
              },
              {
                label: i18n.t('page.dashboard.reservation.changeSchedule'),
                value: 2,
              },
              {
                label: i18n.t(
                  'page.dashboard.reservation.specialCircumstances',
                ),
                value: 3,
              },
              {
                label: i18n.t('page.dashboard.reservation.askedToCancel'),
                value: 4,
              },
              {
                label: i18n.t('page.dashboard.reservation.other'),
                value: 5,
              },
            ]}
            widthType="full"
            selectedValue={props.cancelReason}
            name="cancelReason"
            onChange={props.onChangeCancelReason}
          />
        </FormWrapper>
        <InputWrapper isActive={props.isActiveReasonDesc}>
          <Input.TextArea
            rows={4}
            value={props.reasonDesc}
            placeholder={i18n.t(
              'page.dashboard.reservation.inputCancelReservationReason',
            )}
            onChange={props.onChangeReasonDesc}
          />
        </InputWrapper>
        <AttentionWrapper>
          <AttentionTitle>
            {i18n.t('page.dashboard.reservation.attention')}
          </AttentionTitle>
          <AttentionBlock>
            <Description>
              {i18n.t('page.dashboard.reservation.cancelAttentionFee.0')}
              <Em>
                {i18n.t('page.dashboard.reservation.cancelAttentionFee.1')}
              </Em>
              {i18n.t('page.dashboard.reservation.cancelAttentionFee.2')}
            </Description>
            <Description>
              {i18n.t('page.dashboard.reservation.cancelAttentionUsePoint.0')}
              <Em>
                {i18n.t('page.dashboard.reservation.cancelAttentionUsePoint.1')}
              </Em>
            </Description>
            <Description>
              {i18n.t('page.dashboard.reservation.cancelAttentionReturnPoint')}
            </Description>
            <Description>
              {i18n.t('page.dashboard.reservation.cencelAttentionReturnPoint2')}
            </Description>
          </AttentionBlock>
          <Attention>
            {i18n.t('page.dashboard.reservation.cancelAttention.0')}
            <BrSp />
            {i18n.t('page.dashboard.reservation.cancelAttention.1')}
          </Attention>
        </AttentionWrapper>
      </Section>
      <FloatBottom inbox textAlign="right" hasSidebar>
        <ButtonWrapper>
          <Button
            onClick={props.onClickBackToMessage}
            tag="button"
            colorType="gray"
            buttonType="medium"
            fullWidthSp
            buttonWidthPc="160px"
          >
            {i18n.t('page.shared.cancel')}
          </Button>
        </ButtonWrapper>
        <ButtonWrapper>
          <Button
            onClick={props.onClickSend}
            tag="button"
            colorType="blue"
            buttonType="medium"
            fullWidthSp
            buttonWidthPc="160px"
            disabled={props.isSendButtonDisabled}
          >
            {i18n.t('page.shared.send')}
          </Button>
        </ButtonWrapper>
      </FloatBottom>
    </Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/organisms/ReservationCancelForm/index.jsx